import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import parsePhoneNumber from "libphonenumber-js";
import { trackEvent } from "../../services/analytics_v2";

import { Link } from "react-router-dom";
import { PhoneIcon } from "@heroicons/react/20/solid";

import { Listing, Day } from "../../components/ProviderSearch/types";
import searchById from "../../components/ProviderSearch/providerDirectory/searchById";
import linkCleaner from "../../components/ProviderSearch/linkCleaner";

export default function ShowPlaceDetails() {
  const mapComponentRef = React.useRef<HTMLDivElement | null>(null);
  const [listingLoading, setListingLoading] = React.useState<boolean | null>(
    null,
  );
  const [listing, setListing] = React.useState<Listing | null>(null);
  const [openingHours, setOpeningHours] = React.useState<Hours | null>(null);
  const [map, setMap] = React.useState<google.maps.Map | null>(null);
  const params = useParams();
  const [searchParams, _setSearchParams] = useSearchParams();

  const returnLat = searchParams.get("return_lat");
  const returnLng = searchParams.get("return_lng");
  const returnName = searchParams.get("return_name");

  type Hours = Map<Day, { display: string; open: string; close: string }>;
  const days: Record<Day, string> = {
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
    sun: "Sunday",
  };

  const handleClickPhone = (listing: Listing) => {
    trackEvent("v2ps-show_location-clicked_phone", {
      name: listing.displayName,
    });
  };

  const handleClickBookOnline = (listing: Listing) => {
    trackEvent("v2ps-show_location-clicked_book_online", {
      name: listing.displayName,
    });
  };

  // Once the listing has been loaded, trigger a `displayed_listing` event with
  // the name of the listing that was shown.
  React.useEffect(() => {
    if (!listing) {
      return;
    }
    trackEvent("v2ps-show_location-displayed_listing", {
      name: listing.displayName,
    });
  }, [listing]);

  // Create a Google map on the lat/lng coordinates
  React.useEffect(() => {
    if (
      !listing ||
      !listing.address ||
      !listing.address.lat ||
      !listing.address.lng ||
      !mapComponentRef.current
    ) {
      return;
    }

    const lat = parseFloat(listing.address.lat);
    const lng = parseFloat(listing.address.lng);

    if (map === null) {
      const returnedMap = new window.google.maps.Map(mapComponentRef.current, {
        center: { lat, lng },
        zoom: 16,
        disableDefaultUI: true,
        mapId: "1741826b4ec7ee78",
      });

      new google.maps.Marker({
        position: {
          lat,
          lng,
        },
        map: returnedMap,
        title: listing.displayName,
      });

      setMap(returnedMap);
    } else {
      console.warn("Handle map update");
    }
  }, [listing]);

  // Load listing on page load
  React.useEffect(() => {
    (async () => {
      if (!params.id) {
        return;
      }

      setListingLoading(true);
      const result = await searchById(params.id);
      setListing(result);
      setListingLoading(false);
    })();
  }, [params.id]);

  // Parse opening hours.
  React.useEffect(() => {
    if (!listing) {
      return;
    }
    let hours: Hours = new Map();

    listing.calendar &&
      listing.calendar.openRule.forEach((openRule) => {
        openRule.days.forEach((day) => {
          hours.set(day, {
            display: "Name",
            open: openRule.openFrom.split(":").slice(0, 2).join(":"),
            close: openRule.openTo.split(":").slice(0, 2).join(":"),
          });
        });
      });
    setOpeningHours(hours);
  }, [listing]);

  const parsedPhoneNumber =
    listing && listing.phoneNumber
      ? parsePhoneNumber(listing.phoneNumber, "AU")
      : null;

  const renderedOpeningHours =
    openingHours &&
    openingHours?.size > 0 &&
    (Object.entries(days) as [Day, string][]).map(([d, name]) => {
      return (
        <tr key={d} className="border-t-2 border-gray-200">
          <th className="text-left p-1 pr-8">{name}</th>
          <td className="text-left p-1 pr-8">{openingHours.get(d)?.open}</td>
          <td className="text-left p-1 pr-8">{openingHours.get(d)?.close}</td>
        </tr>
      );
    });

  return (
    <div className="p-4 md:p-8 flex flex-col md:flex-row items-stretch gap-4 md:gap-8">
      <div className="basis-1/2 md:order-last">
        <div
          ref={mapComponentRef}
          className="w-full h-full min-h-[250px] md:min-h-[500px]"
        />
      </div>
      <div className="basis-1/2 flex flex-col flex-stretch">
        <div className="flex flex-wrap items-center gap-2">
          <Link className="underline shrink-0" to={`/provider_search`}>
            Back to search
          </Link>
          {returnLat && returnLng && returnName && (
            <>
              <span>&gt;</span>
              <Link
                className="underline shrink-0"
                to={`/provider_search/place?&lat=${returnLat}&lng=${returnLng}&name=${returnName}`}
              >
                {returnName}
              </Link>
            </>
          )}
          <span>&gt;</span>
          {listing && <span className="shrink-0">{listing.displayName}</span>}
        </div>

        {listingLoading === true && (
          <p className="my-4 font-italic">Loading listing...</p>
        )}
        {listingLoading === false &&
          listing &&
          Object.keys(listing).length === 0 && (
            <p className="my-4">
              Unable to find a listing for id {params.id}, please try another{" "}
              <Link className="underline" to={`/provider_search`}>
                search
              </Link>
              .
            </p>
          )}
        {listingLoading === false &&
          listing &&
          Object.keys(listing).length != 0 && (
            <div className="bg-white p-4 mt-4 md:mt-8 flex-grow flex flex-col">
              <h1 className="text-4xl mb-4 md:mb-8">{listing.displayName}</h1>
              <div className="flex-grow">
                {listing.address && (
                  <address className="m-0 not-italic flex-grow mb-4 text-lg">
                    {listing.address.addressLine1 && (
                      <div>{listing.address.addressLine1}</div>
                    )}
                    {listing.address.addressLine2 && (
                      <div>{listing.address.addressLine2}</div>
                    )}
                    {listing.address.addressLine3 && (
                      <div>{listing.address.addressLine3}</div>
                    )}
                    {listing.address.suburb && (
                      <div>{listing.address.suburb}</div>
                    )}
                  </address>
                )}
                {parsedPhoneNumber && (
                  <>
                    <a
                      className="my-4 flex items-center gap-2 block"
                      href={`tel:${parsedPhoneNumber.formatNational()}`}
                      onClick={() => handleClickPhone(listing)}
                    >
                      <PhoneIcon width={"20px"} />{" "}
                      {parsedPhoneNumber.formatNational()}
                    </a>
                    <p className="mt-2 italic max-w-prose">
                      Clicking on this phone number will open a calling
                      application to call the number
                    </p>
                  </>
                )}
                {openingHours && (
                  <div className="my-6">
                    <h3>Opening hours</h3>
                    <table>
                      <thead>
                        <tr>
                          <th className="text-left p-1 pr-8">Day</th>
                          <th className="text-left p-1 pr-8">Opens at</th>
                          <th className="text-left p-1 pr-8">Closes at</th>
                        </tr>
                      </thead>
                      <tbody>{renderedOpeningHours}</tbody>
                    </table>
                  </div>
                )}
                {listing.website && (
                  <div className="mt-8">
                    <a
                      href={linkCleaner(listing.website)}
                      className="btn btn-primary block w-full md:w-96 no-external-link-icon"
                      target="_blank"
                      onClick={() => handleClickBookOnline(listing)}
                    >
                      Book an eye test online
                    </a>
                    <p className="mt-2 italic max-w-prose">
                      Clicking on this book button will take you to a website
                      where you can make a booking
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
